import clsx from 'clsx';
import { type ChangeEvent, type FC, useEffect, useId, useRef, useState } from 'react';

interface SwitchProps {
  rightContentText: string;
  leftContentText: string;
  readOnly?: boolean;
  label?: string;
  className?: string;
  checked?: boolean;
  id?: string;
  onClick?: (e: ChangeEvent<HTMLInputElement>) => void;
}

export const Switch: FC<SwitchProps> = ({
  rightContentText,
  leftContentText,
  checked,
  onClick,
  readOnly = false,
  id,
  label,
  className,
}) => {
  const internalId = useId();
  const leftTextRef = useRef<HTMLSpanElement>(null);
  const rightTextRef = useRef<HTMLSpanElement>(null);
  const [leftTextWidth, setLeftTextWidth] = useState<number>(0);
  const [rightTextWidth, setRightTextWidth] = useState<number>(0);

  useEffect(() => {
    const measureWidths = () => {
      if (leftTextRef.current && rightTextRef.current) {
        const leftWidth = leftTextRef.current.getBoundingClientRect().width;
        const rightWidth = rightTextRef.current.getBoundingClientRect().width;
        setLeftTextWidth(leftWidth);
        setRightTextWidth(rightWidth);
      }
    };

    requestAnimationFrame(() => {
      measureWidths();
    });
  }, [leftContentText, rightContentText]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (readOnly) {
      return;
    }
    onClick?.(e);
  };

  const maxTextWidth = Math.max(leftTextWidth, rightTextWidth);
  const thumbSize = 16;
  const gap = 6;
  const innerPadding = 8;
  const borderWidth = 2; // border z obu stron

  // Szerokość widocznego kontenera powinna pokazywać:
  // unchecked: [innerPadding][text][gap][thumb][innerPadding]
  // checked: [innerPadding][thumb][gap][text][innerPadding]
  const visibleWidth = innerPadding + maxTextWidth + gap + thumbSize + innerPadding + borderWidth;

  // Przesunięcie: odwrócony kierunek - na początku schowamy lewy tekst
  const translateX = checked ? 0 : -(maxTextWidth + gap);

  return (
    <div className={clsx('flex items-center gap-4', className)}>
      {label && (
        <label className="cursor-pointer text-s18" htmlFor={id || internalId}>
          {label}
        </label>
      )}
      <div
        className={clsx(
          'relative overflow-hidden rounded-full text-s11-sb',
          'h-[28px] border border-border-primary',
          !readOnly && 'bg-surface-primary hover:bg-surface-container-lighter',
          readOnly && 'bg-surface-disabled',
          checked ? 'pl-1' : 'pr-1',
          !readOnly && 'cursor-pointer',
          readOnly && 'cursor-not-allowed',
        )}
        style={{
          width: maxTextWidth > 0 ? `${visibleWidth}px` : '100px',
        }}
      >
        {/* Hidden measurement spans */}
        <span
          className="text-sm pointer-events-none invisible absolute leading-none font-medium whitespace-nowrap"
          ref={leftTextRef}
        >
          {leftContentText}
        </span>
        <span
          className="text-sm pointer-events-none invisible absolute leading-none font-medium whitespace-nowrap"
          ref={rightTextRef}
        >
          {rightContentText}
        </span>

        <input
          aria-checked={checked}
          aria-disabled={readOnly}
          aria-label={label}
          checked={checked}
          className="absolute inset-0 z-50 h-full w-full cursor-pointer opacity-0"
          disabled={readOnly}
          id={id || internalId}
          readOnly={readOnly}
          role="switch"
          type="checkbox"
          onChange={handleChange}
        />

        {/* Sliding container with all content */}
        <div
          className="flex h-full items-center gap-2 px-0.5 transition-transform duration-300 ease-in-out"
          style={{
            transform: `translateX(${translateX}px)`,
          }}
        >
          {/* Left text + Thumb wrapper */}
          <div
            className={clsx(
              'flex items-center gap-2 rounded-full',
              readOnly ? 'bg-surface-disabled' : checked && 'bg-surface-highlight',
            )}
          >
            {/* Left text */}
            <span
              className={clsx(
                checked ? 'py-1 pl-2 text-text-brand-darker' : 'text-text-secondary',
                readOnly && 'bg-surface-disabled',
                'pointer-events-none inline-block flex-shrink-0 text-left leading-none whitespace-nowrap transition-colors duration-300 select-none',
              )}
              style={{
                width: maxTextWidth > 0 ? `${maxTextWidth}px` : 'auto',
              }}
            >
              {leftContentText}
            </span>

            {/* Thumb - centered between texts */}
            <span
              className={clsx(
                'shadow-md pointer-events-none z-20 h-[16px] w-[16px] flex-shrink-0 rounded-full transition-colors duration-300',
                checked ? 'bg-surface-brand-darker' : 'bg-surface-status-grey-darkest',
              )}
            />
          </div>

          {/* Right text */}
          <span
            className={clsx(
              'pointer-events-none z-10 inline-block flex-shrink-0 text-right leading-none whitespace-nowrap transition-colors duration-300 select-none',
              !checked ? 'text-text-secondary' : 'text-text-brand-darker',
            )}
            style={{
              width: maxTextWidth > 0 ? `${maxTextWidth}px` : 'auto',
            }}
          >
            {rightContentText}
          </span>
        </div>
      </div>
    </div>
  );
};
